import React, { useState } from 'react';
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin, Link2, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { AgentConfig } from '../config/agents';
import { useForm } from 'react-hook-form';

interface ContactProps {
  agent: AgentConfig;
}

type FormData = {
  name: string;
  email: string;
  phone: string;
  message: string;
};

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function Contact({ agent }: ContactProps) {
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<FormData>();

  const encode = (data: { [key: string]: string }) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  }

  const onSubmit = async (data: FormData) => {
    try {
      setFormStatus('submitting');
      
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'contact',
          ...data
        })
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      setFormStatus('success');
      reset();
      
      setTimeout(() => {
        setFormStatus('idle');
      }, 5000);
    } catch (error) {
      setFormStatus('error');
      setErrorMessage('There was a problem submitting your form. Please try again.');
      
      setTimeout(() => {
        setFormStatus('idle');
        setErrorMessage('');
      }, 5000);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
        <div className="bg-navy-900 text-white p-6 lg:p-8 rounded-lg animate-fade-in">
          <h1 className="text-4xl font-bold mb-6 animate-slide-right">Let's Get In Touch</h1>
          <div className="space-y-6">
            <div className="animate-slide-right animate-delay-100">
              <h2 className="text-xl font-semibold mb-2">Office Location</h2>
              <div className="flex items-start text-accent">
                <MapPin className="w-5 h-5 mr-3 mt-1 flex-shrink-0" />
                <p>
                  {agent.office.address}<br />
                  {agent.office.city}, {agent.office.state} {agent.office.zip}<br />
                  {agent.office.country}
                </p>
              </div>
            </div>
            <div className="animate-slide-right animate-delay-200">
              <h2 className="text-xl font-semibold mb-2">Contact Information</h2>
              <div className="space-y-3">
                <div className="flex items-center text-accent">
                  <Phone className="w-5 h-5 mr-3" />
                  <p>{agent.phone}</p>
                </div>
                <div className="flex items-center text-accent">
                  <Mail className="w-5 h-5 mr-3" />
                  <p>{agent.email}</p>
                </div>
              </div>
            </div>
            <div className="animate-slide-right animate-delay-300">
              <h2 className="text-xl font-semibold mb-2">Office Hours</h2>
              <p className="text-accent">
                Monday - Friday: 9:00 AM - 5:00 PM<br />
                Saturday: By Appointment<br />
                Sunday: Closed
              </p>
            </div>
            <div>
              <h2 className="text-xl font-semibold mb-2">Connect With Me</h2>
              <div className="flex space-x-4">
                {agent.socialLinks.facebook && (
                  <a
                    href={agent.socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                    aria-label="Facebook Profile"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                )}
                {agent.socialLinks.instagram && (
                  <a
                    href={agent.socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                    aria-label="Instagram Profile"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                )}
                {agent.socialLinks.linkedin && (
                  <a
                    href={agent.socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                    aria-label="LinkedIn Profile"
                  >
                    <Linkedin className="w-6 h-6" />
                  </a>
                )}
                {agent.socialLinks.zillow && (
                  <a
                    href={agent.socialLinks.zillow}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                    aria-label="Zillow Profile"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 23.283 25.577"
                      className="w-6 h-6"
                      fill="currentColor"
                    >
                      <g fillRule="nonzero">
                        <path d="m15.743 6.897c.117-.026.169.013.24.091.403.448 1.691 2.021 2.041 2.45.065.078.02.163-.032.208-2.6 2.028-5.493 4.901-7.105 6.955-.032.046-.006.046.02.039 2.808-1.209 9.405-3.14 12.376-3.679v-3.763l-11.628-9.198-11.648 9.191v4.114c3.607-2.144 11.953-5.466 15.736-6.408z" />
                        <path d="m6.279 22.705c-.097.052-.176.039-.254-.039l-2.171-2.587c-.058-.072-.065-.111.013-.221 1.678-2.457 5.103-6.286 7.287-7.904.039-.026.026-.059-.02-.039-2.275.741-8.742 3.523-11.134 4.875v8.787h23.277v-8.462c-3.172.539-12.675 3.367-16.998 5.59z" />
                      </g>
                    </svg>
                  </a>
                )}
                <a
                  href={agent.socialLinks.retter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                  aria-label="Retter & Company Profile"
                >
                  <Link2 className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="animate-fade-in animate-delay-100">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-2xl font-bold text-navy-900 mb-4 animate-slide-right">Send a Message</h2>
            
            {/* Success Message */}
            {formStatus === 'success' && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md flex items-center text-green-700">
                <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <p>Thank you for your message! We'll get back to you soon.</p>
              </div>
            )}
            
            {/* Error Message */}
            {formStatus === 'error' && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <p>{errorMessage}</p>
              </div>
            )}
            
            <form 
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit(onSubmit)} 
              className="space-y-4"
              noValidate
            >
              {/* These hidden inputs are required for Netlify forms */}
              <input type="hidden" name="form-name" value="contact" />
              <div hidden>
                <input name="bot-field" />
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name', { 
                    required: 'Name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' }
                  })}
                  className={`w-full px-4 py-2 border rounded-md transition-colors duration-200 ${
                    errors.name 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-accent focus:border-accent'
                  }`}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address',
                    },
                  })}
                  className={`w-full px-4 py-2 border rounded-md transition-colors duration-200 ${
                    errors.email 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-accent focus:border-accent'
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  {...register('phone', { 
                    required: 'Phone number is required',
                    pattern: {
                      value: /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
                      message: 'Please enter a valid phone number'
                    }
                  })}
                  className={`w-full px-4 py-2 border rounded-md transition-colors duration-200 ${
                    errors.phone 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-accent focus:border-accent'
                  }`}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  rows={4}
                  {...register('message', { 
                    required: 'Message is required',
                    minLength: { value: 10, message: 'Message must be at least 10 characters' }
                  })}
                  className={`w-full px-4 py-2 border rounded-md transition-colors duration-200 ${
                    errors.message 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-accent focus:border-accent'
                  }`}
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.message.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting || formStatus === 'submitting'}
                className={`w-full flex items-center justify-center py-2 px-4 rounded-md text-white transition-all duration-200 ${
                  isSubmitting || formStatus === 'submitting'
                    ? 'bg-accent/70 cursor-not-allowed'
                    : 'bg-accent hover:bg-accent/90 hover:scale-[1.02] active:scale-[0.98]'
                }`}
                aria-label={isSubmitting ? 'Sending message...' : 'Send message'}
              >
                {(isSubmitting || formStatus === 'submitting') ? (
                  <>
                    <Loader className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}